import * as Pages from "@/components/Pages";
import * as Paper from "@/components/PaperContainer";
import * as People from "@/models/people";
import * as Projects from "@/models/projects";
import * as Spaces from "@/models/spaces";
import * as React from "react";
import Api from "@/api";

import { useMe } from "@/contexts/CurrentCompanyContext";
import { compareIds } from "@/routes/paths";
import { useNavigate, useSearchParams } from "react-router";
import { useLoadedData } from "./loader";

import { PermissionLevels } from "@/features/Permissions";
import { applyAccessLevelConstraints, initialAccessLevels } from "@/features/Permissions/AccessFields";
import { AccessSelectors } from "@/features/projects/AccessSelectors";
import { AccessLevelSummary, Forms, ProjectTemplateSelection, SecondaryButton } from "turboui";

import { usePaths } from "@/routes/paths";
export function Page() {
  return (
    <Pages.Page title="New Project">
      <Paper.Root size="small">
        <Navigation />
        <PageTitle />
        <Form />
      </Paper.Root>
    </Pages.Page>
  );
}

function PageTitle() {
  const { spaceID, space } = useLoadedData();

  if (spaceID && space) {
    return <h1 className="mb-4 font-bold text-3xl text-center">Start a new project in {space!.name}</h1>;
  } else {
    return <h1 className="mb-4 font-bold text-3xl text-center">Start a new project</h1>;
  }
}

function Navigation() {
  const { backPath, backPathName } = useLoadedData();
  if (!backPath) return null;

  return <Paper.NavigateBack to={backPath} title={backPathName} />;
}

function Form() {
  const paths = usePaths();
  const me = useMe()!;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [add] = Projects.useCreateProject();
  const [createFromTemplate] = Api.project_templates.useCreateProject();
  const { space, spaces, spaceOptions, goal, goals, templates } = useLoadedData();
  const search = People.usePeopleSearch(People.CompanyWideSearchScope);
  const initialTemplateID = templates.find(
    (template) => template.id === searchParams.get("templateId") && template.space.id === space?.id,
  )?.id;

  const form = Forms.useForm({
    fields: {
      name: "",
      space: space?.id,
      template: initialTemplateID ?? "",
      startDate: "",
      champion: "",
      reviewer: "",
      goal: goal,
      access: initialAccessLevels(null, findParentAccessLevel(space, spaces, space?.id)),
      showAdvancedAccess: false,
    },
    onChange: ({ field, newValues }) => {
      const parentAccessLevel = findParentAccessLevel(space, spaces, newValues.space);

      if (field === "space") {
        newValues.access = initialAccessLevels(null, parentAccessLevel);
        newValues.template = "";
        newValues.startDate = "";
      } else {
        newValues.access = applyAccessLevelConstraints(newValues.access, parentAccessLevel);
      }
    },
    validate: (addError) => {
      if (!form.values.template && compareIds(form.values.champion, form.values.reviewer)) {
        addError("reviewer", "Can't be the same as the champion");
      }
      if (!form.values.space) {
        addError("space", "Space is required");
      }
    },
    submit: async () => {
      const projectInput = {
        name: form.values.name,
        championId: form.values.champion,
        reviewerId: form.values.reviewer,
        spaceId: form.values.space!,
        goalId: form.values.goal?.id,
        anonymousAccessLevel: form.values.access.anonymous,
        companyAccessLevel: form.values.access.companyMembers,
        spaceAccessLevel: form.values.access.spaceMembers,
      };
      const res = form.values.template
        ? await createFromTemplate({
            name: projectInput.name,
            spaceId: projectInput.spaceId,
            goalId: projectInput.goalId,
            anonymousAccessLevel: projectInput.anonymousAccessLevel,
            companyAccessLevel: projectInput.companyAccessLevel,
            spaceAccessLevel: projectInput.spaceAccessLevel,
            templateId: form.values.template,
            startDate: form.values.startDate,
          })
        : await add(projectInput);

      navigate(paths.projectPath(res.project.id!));
    },
    onError: () => {
      form.actions.addErrors({
        form: "The project could not be created. Check the form and try again.",
      });
    },
  });

  return (
    <Forms.Form form={form}>
      <Paper.Body minHeight="300px">
        <Forms.FieldGroup>
          <Forms.TextInput label="Project Name" field="name" placeholder="e.g. HR System Update" autoFocus required />
          <Forms.SelectBox label="Space" field="space" options={spaceOptions} required />
          <ProjectTemplateSelection
            spaceId={form.values.space}
            templates={templates.map((template) => ({
              id: template.id,
              name: template.name,
              spaceId: template.space.id,
              inactivePeopleSummary: template.inactivePeopleSummary,
              inactiveDiscussionCount: template.inactiveDiscussionCount,
            }))}
          />
          <Forms.SelectGoal label="Goal" field="goal" goals={goals} required={false} />

          {!form.values.template && (
            <Forms.FieldGroup layout="grid">
              <SelectChampion me={me} search={search} />
              <SelectReviewer me={me} search={search} />
            </Forms.FieldGroup>
          )}
        </Forms.FieldGroup>

        <PrivacyLevel />
        <Forms.FormError message={form.errors.form} />
      </Paper.Body>

      <Forms.Submit saveText="Add Project" layout="centered" buttonSize="lg" />
    </Forms.Form>
  );
}

function SelectChampion({ me, search }: { me: People.Person; search: (query: string) => Promise<People.Person[]> }) {
  return <Forms.SelectPerson label="Champion" field="champion" searchFn={search} default={me} />;
}

function SelectReviewer({ me, search }: { me: People.Person; search: (query: string) => Promise<People.Person[]> }) {
  return (
    <Forms.SelectPerson
      label="Reviewer"
      field="reviewer"
      searchFn={search}
      allowEmpty={true}
      emptyLabel="No reviewer"
      default={me?.manager}
      required={false}
    />
  );
}

function PrivacyLevel() {
  const [isAdvanced] = Forms.useFieldValue<boolean>("showAdvancedAccess");

  return (
    <Paper.DimmedSection>
      <div className="flex items-center justify-between">
        <PrivacyLevelTitle field={"access"} />
        <PrivacyEdit />
      </div>

      {isAdvanced && <AccessSelectors />}
    </Paper.DimmedSection>
  );
}

function PrivacyLevelTitle({ field }: { field: string }) {
  const [anonymous = PermissionLevels.NO_ACCESS] = Forms.useFieldValue<number>(`${field}.anonymous`);
  const [company = PermissionLevels.NO_ACCESS] = Forms.useFieldValue<number>(`${field}.companyMembers`);
  const [space = PermissionLevels.NO_ACCESS] = Forms.useFieldValue<number>(`${field}.spaceMembers`);

  return (
    <AccessLevelSummary
      resourceType="project"
      tense="future"
      anonymous={anonymous}
      company={company}
      space={space}
      hideIcon
    />
  );
}

function PrivacyEdit() {
  const [isAdvanced, setIsAdvanced] = Forms.useFieldValue<boolean>("showAdvancedAccess");
  if (isAdvanced) return null;

  return (
    <SecondaryButton size="xs" onClick={() => setIsAdvanced(true)} testId="edit-access-levels">
      Edit
    </SecondaryButton>
  );
}

function findParentAccessLevel(
  space: Spaces.Space | undefined,
  spaces: Spaces.Space[] | undefined,
  spaceId: string | null | undefined,
) {
  if (spaces && spaceId) {
    return spaces.find((s) => compareIds(s.id, spaceId))!.accessLevels!;
  } else if (space) {
    return space.accessLevels!;
  } else {
    return {
      __typename: "access_levels" as const,
      public: PermissionLevels.NO_ACCESS,
      company: PermissionLevels.COMMENT_ACCESS,
      space: PermissionLevels.COMMENT_ACCESS,
    };
  }
}
